import { useEffect, useState } from "react";
import { type Address, createPublicClient, http, formatUnits } from "viem";
import { ADDRESSES, V2_FACTORY_ABI, V2_PAIR_ABI, ERC20_ABI } from "../config/contracts";

const RPC = "https://rpc.jasmyscan.net";

interface TickerToken {
  symbol: string;
  reserveJasmy: string;
}

function getClient() {
  return createPublicClient({
    chain: {
      id: 680,
      name: "JasmyChain",
      nativeCurrency: { name: "JASMY", symbol: "JASMY", decimals: 18 },
      rpcUrls: { default: { http: [RPC] } },
    },
    transport: http(RPC),
  });
}

function TickerItem({ symbol, reserveJasmy }: TickerToken) {
  return (
    <span className="inline-flex items-center gap-2 px-4 whitespace-nowrap text-xs">
      <span className="font-semibold text-text-primary">{symbol}</span>
      <span className="text-text-secondary">{reserveJasmy} JASMY</span>
      <span className="text-text-tertiary mx-2">|</span>
    </span>
  );
}

export function TokenTicker() {
  const [tokens, setTokens] = useState<TickerToken[]>([]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      const client = getClient();
      try {
        const count = Number(
          (await client.readContract({
            address: ADDRESSES.V2_FACTORY,
            abi: V2_FACTORY_ABI,
            functionName: "allPairsLength",
          })) as bigint
        );

        const limit = Math.min(count, 20);
        const results: TickerToken[] = [];
        const wjasmy = ADDRESSES.WJASMY.toLowerCase();

        for (let i = 0; i < limit; i++) {
          if (cancelled) return;
          try {
            const pairAddr = (await client.readContract({
              address: ADDRESSES.V2_FACTORY,
              abi: V2_FACTORY_ABI,
              functionName: "allPairs",
              args: [BigInt(i)],
            })) as Address;

            const [t0, t1, reserves] = await Promise.all([
              client.readContract({ address: pairAddr, abi: V2_PAIR_ABI, functionName: "token0" }),
              client.readContract({ address: pairAddr, abi: V2_PAIR_ABI, functionName: "token1" }),
              client.readContract({ address: pairAddr, abi: V2_PAIR_ABI, functionName: "getReserves" }),
            ]);

            const token0 = (t0 as Address).toLowerCase();
            const token1 = (t1 as Address).toLowerCase();
            const res = reserves as [bigint, bigint, number];

            // Figure out which side is WJASMY and which is the other token
            let otherAddr: Address;
            let jasmyReserve: bigint;

            if (token0 === wjasmy) {
              otherAddr = t1 as Address;
              jasmyReserve = res[0];
            } else if (token1 === wjasmy) {
              otherAddr = t0 as Address;
              jasmyReserve = res[1];
            } else {
              continue; // skip non-JASMY pairs
            }

            const symbol = (await client.readContract({
              address: otherAddr,
              abi: ERC20_ABI,
              functionName: "symbol",
            })) as string;

            const formatted = parseFloat(formatUnits(jasmyReserve, 18));
            const display =
              formatted >= 1_000_000
                ? (formatted / 1_000_000).toFixed(1) + "M"
                : formatted >= 1_000
                  ? (formatted / 1_000).toFixed(1) + "K"
                  : formatted.toFixed(0);

            results.push({ symbol, reserveJasmy: display });
          } catch {
            continue;
          }
        }

        if (!cancelled && results.length > 0) {
          setTokens(results);
        }
      } catch {
        // Factory unreachable
      }
    }

    load();
    return () => { cancelled = true; };
  }, []);

  if (tokens.length === 0) return null;

  const items = [...tokens, ...tokens];

  return (
    <div className="w-full bg-[#050710] border-b border-border overflow-hidden h-8 flex items-center">
      <div className="ticker-scroll">
        {items.map((token, i) => (
          <TickerItem key={`${token.symbol}-${i}`} {...token} />
        ))}
      </div>
    </div>
  );
}
